import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Instagram, Facebook, ChevronDown, RefreshCw, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, bestsellersRes, saleRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/products/bestsellers`),
          axios.get(`${API}/products/sale`)
        ]);
        setProducts(productsRes.data);
        setBestsellers(bestsellersRes.data);
        setSaleProducts(saleRes.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hookahs = products.filter(p => p.category === 'hookah');
  const sets = products.filter(p => p.category === 'set');

  const featuredProducts = [...hookahs.slice(0, 3), ...sets.slice(0, 3)];
  // Use bestsellers if available, otherwise fallback to sets
  const displayBestsellers = bestsellers.length > 0 ? bestsellers.slice(0, 6) : sets.slice(0, 6);

  const scrollToProducts = () => {
    document.getElementById('featured-products')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 via-black to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-zinc-800/20 via-transparent to-transparent" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-zinc-700/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 mb-10 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-black hover:bg-zinc-200 font-semibold px-8 py-6 text-lg"
              onClick={() => navigate('/hookahs')}
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-900 font-semibold px-8 py-6 text-lg"
              asChild
            >
              <a
                href="https://www.instagram.com/_shishavibes._/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="mr-2 h-5 w-5" />
                {t.hero.instagram}
              </a>
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToProducts}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown className="h-8 w-8" />
        </button>
      </section>

      {/* Hot Deals / Akciók most */}
      {saleProducts.length > 0 && (
        <section className="py-10 md:py-14 px-4 bg-gradient-to-b from-red-950/20 to-black border-y border-orange-500/20" data-testid="home-sale-band">
          <div className="container mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-xl">
                  <Sparkles className="h-6 w-6 md:h-7 md:w-7 text-black" />
                </div>
                <h2 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 bg-clip-text text-transparent">
                  {language === 'hu' ? 'Akciók most' : 'Hot Deals Now'}
                </h2>
              </div>
              <Button
                variant="outline"
                className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10 hover:text-orange-200"
                onClick={() => navigate('/sale')}
                data-testid="home-view-all-sales"
              >
                {language === 'hu' ? 'Összes akció' : 'View all'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {saleProducts.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-8 md:mb-12">
            {t.categories.title}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {/* Hookahs Category */}
            <div
              onClick={() => navigate('/hookahs')}
              className="group relative h-48 sm:h-64 md:h-80 rounded-xl overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="relative h-full flex flex-col items-center justify-center p-4 md:p-6 text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-2xl md:text-4xl">💨</span>
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">{t.categories.hookahs}</h3>
                <p className="text-zinc-400 text-sm md:text-base hidden sm:block">{t.categories.hookahsDesc}</p>
                <span className="mt-1 md:mt-2 text-xs md:text-sm text-zinc-500">{hookahs.length} {t.products.items}</span>
              </div>
            </div>

            {/* Complete Sets Category */}
            <div
              onClick={() => navigate('/sets')}
              className="group relative h-48 sm:h-64 md:h-80 rounded-xl overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-700 to-zinc-800" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="relative h-full flex flex-col items-center justify-center p-4 md:p-6 text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-2xl md:text-4xl">📦</span>
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">{t.categories.sets}</h3>
                <p className="text-zinc-400 text-sm md:text-base hidden sm:block">{t.categories.setsDesc}</p>
                <span className="mt-1 md:mt-2 text-xs md:text-sm text-zinc-500">{sets.length} {t.products.items}</span>
              </div>
            </div>

            {/* Accessories Category */}
            <div
              onClick={() => navigate('/accessories')}
              className="group relative h-48 sm:h-64 md:h-80 rounded-xl overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-600 to-zinc-700" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="relative h-full flex flex-col items-center justify-center p-4 md:p-6 text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-white/10 flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                  <span className="text-2xl md:text-4xl">🔧</span>
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">{t.categories.accessories}</h3>
                <p className="text-zinc-400 text-sm md:text-base hidden sm:block">{t.categories.accessoriesDesc}</p>
                <span className="mt-1 md:mt-2 text-xs md:text-sm text-zinc-500">{products.filter(p => p.category === 'accessory').length} {t.products.items}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="featured-products" className="py-20 px-4 bg-zinc-950">
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t.products.featured}
            </h2>
            <Button
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
              onClick={() => navigate('/hookahs')}
            >
              {t.filters.all}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-20">
              <RefreshCw className="h-8 w-8 animate-spin text-zinc-500 mx-auto mb-4" />
              <p className="text-zinc-500">{t.common.loading}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bestsellers Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t.products.bestsellers}
            </h2>
            <Button
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
              onClick={() => navigate('/bestsellers')}
              data-testid="home-bestsellers-view-all"
            >
              {t.filters.all}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          {!loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
              {displayBestsellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 to-zinc-800" />
            <div className="relative py-16 px-8 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t.footer.followUs}
              </h2>
              <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
                @_shishavibes._
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button
                  size="lg"
                  className="bg-white text-black hover:bg-zinc-200"
                  asChild
                >
                  <a
                    href="https://www.facebook.com/profile.php?id=61583331609027"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-facebook-link"
                  >
                    <Facebook className="mr-2 h-5 w-5" />
                    Facebook
                  </a>
                </Button>
                <Button
                  size="lg"
                  className="bg-white text-black hover:bg-zinc-200"
                  asChild
                >
                  <a
                    href="https://www.instagram.com/_shishavibes._/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-instagram-link"
                  >
                    <Instagram className="mr-2 h-5 w-5" />
                    Instagram
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
